import { DropUndefined, ICustomer } from '@monofront/types';
import {
  ACTIONS,
  AddressFormEvent,
  Context,
  MultiStepFormMachineConfig,
  MultiStepFormStep,
  MultiStepFormStepType,
} from './types';
import invariant from 'tiny-invariant';

const generateActionsFromSliceType = (sliceType: MultiStepFormStepType) => {
  const actions: Array<string> = [];

  switch (sliceType) {
    case MultiStepFormStepType.Intro:
      actions.push(ACTIONS.START_FORM);
      break;
    case MultiStepFormStepType.MultiSelectCollection:
    case MultiStepFormStepType.AddressForm:
      actions.push(ACTIONS.SAVE_FORM_DATA);
      break;
    default:
      break;
  }

  return actions;
};

export const generateConfig = ({
  id,
  steps,
  customer,
  getCollectionPreferences,
  handleAddressForm,
}: {
  id: string;
  steps: Array<MultiStepFormStep>;
  customer: ICustomer | null;
  getCollectionPreferences: () => Promise<Array<string>>;
  handleAddressForm: (address: AddressFormEvent) => void;
}): MultiStepFormMachineConfig => {
  const context = {} as Partial<Context>;
  const states = {} as DropUndefined<
    Partial<MultiStepFormMachineConfig['states']>
  >;

  steps.forEach((step, i) => {
    const { uid: currentUID, type } = step;
    const isFirst = i === 0;
    const isLast = i === steps.length - 1;
    const prevUID = isFirst ? null : steps[i - 1].uid;
    const nextUID = isLast ? null : steps[i + 1].uid;

    if (isFirst && currentUID !== 'page_loader') {
      context.firstStepID = currentUID;
    } else if (isFirst && currentUID === 'page_loader' && nextUID) {
      context.firstStepID = nextUID;
    }

    if (currentUID === 'page_loader') {
      invariant(nextUID, 'page_loader should be the first of multiples steps');

      states.page_loader = {
        invoke: {
          src: async () => {
            const collectionPreferences = await getCollectionPreferences();
            const { street, postalCode, city } = customer?.address ?? {};
            const label =
              street != null && postalCode != null && city != null
                ? `${street}, ${postalCode}, ${city}`
                : '';

            return {
              savedCollections: collectionPreferences,
              savedAddress: {
                ...customer?.address,
                label: label,
              },
            };
          },
          onDone: {
            target: nextUID,
            actions: [ACTIONS.INIT_FORM_DATA],
          },
          onError: {
            actions: [ACTIONS.EXIT_FORM],
          },
        },
      };
    } else {
      states[currentUID] = {
        on: {
          [ACTIONS.NEXT]: nextUID
            ? {
                target: nextUID,
                actions: [...generateActionsFromSliceType(type)],
              }
            : { actions: [ACTIONS.FINISH_FORM] },
          [ACTIONS.PREVIOUS]: prevUID
            ? {
                target: prevUID,
                actions: [...generateActionsFromSliceType(type)],
              }
            : { actions: [ACTIONS.EXIT_FORM] },
          [ACTIONS.SELECT_ADDRESS]: {
            actions: [
              (context) => context,
              (_, event) => handleAddressForm(event.payload),
            ],
          },
          [ACTIONS.EXIT_FORM]: { actions: [ACTIONS.EXIT_FORM] },
          [ACTIONS.FINISH_FORM]: { actions: [ACTIONS.FINISH_FORM] },
        },
      };
    }
  });

  return {
    id,
    initial: 'page_loader',
    context: context as Context,
    states: states as MultiStepFormMachineConfig['states'],
  };
};
